import { useEffect } from 'react';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookie-consent-foundry.css';
import './cookie-consent-shed.css';
import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * CookieConsent — vanilla-cookieconsent v3 integration for the marketing
 * surface.
 *
 * Posture (per docs/legal/cookie-policy.md):
 *   - `necessary` category — always on, no toggle. Auth, security, basic
 *     site function.
 *   - `analytics` category — opt-in by default in EU / UK / EEA (where
 *     consent is required before any non-essential cookie fires); opt-out
 *     elsewhere (US / AU / CA — where notice + opt-out is sufficient).
 *     The library autodetects via `mode: 'opt-in' | 'opt-out'` chosen at
 *     mount time using a coarse Accept-Language / timezone heuristic.
 *
 * The marketing surface today ships zero analytics. This component is the
 * compliance foundation: when PostHog / GA / etc. are wired later, they
 * gate behind `CookieConsent.acceptedCategory('analytics')` rather than
 * loading at boot.
 *
 * Visual posture: the Foundry CSS overrides (cookie-consent-foundry.css)
 * style the banner as a calm bottom-rail bar — Geist Sans body, Geist Mono
 * uppercase eyebrow, race-yellow accept button, ink-on-paper neutral
 * decline. No animated bib stamps, no scribble underlines. Reads as part
 * of the chrome, not a third-party widget paste-in.
 *
 * Mounted from MarketingV2App. The banner persists across navigation
 * (vanilla-cookieconsent handles its own DOM lifecycle and storage).
 */
export default function CookieConsentBanner({ theme = 'foundry' }: { theme?: 'foundry' | 'shed' }) {
  useEffect(() => {
    const previous = document.body.classList.contains('shed-consent');
    document.body.classList.toggle('shed-consent', theme === 'shed');
    return () => { document.body.classList.toggle('shed-consent', previous); };
  }, [theme]);
  useEffect(() => {
    // Coarse jurisdiction detection — opt-in for EU/UK/EEA, opt-out elsewhere.
    // This is intentionally conservative: any locale uncertainty defaults to
    // opt-in, since opt-in is the stricter posture.
    const isEuUk = detectLikelyEuUk();
    const mode: 'opt-in' | 'opt-out' = isEuUk ? 'opt-in' : 'opt-out';

    CookieConsent.run({
      // Use `false` for opt-in (prior consent required); `true` for opt-out
      // (consent assumed; user can withdraw).
      autoShow: true,
      mode,
      revision: 1,
      cookie: {
        name: 'abl_cc_consent',
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: mode === 'opt-out',
          readOnly: false,
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: '_gid' },
              { name: 'ph_session_id' },
              { name: /^ph_phc/ },
            ],
          },
        },
      },
      guiOptions: {
        consentModal: {
          layout: 'bar inline',
          position: 'bottom',
          equalWeightButtons: false,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'Cookies',
              description:
                "We use a small number of necessary cookies to keep the site running, and (with your permission) analytics cookies to understand which pages help visitors most. You can change this any time. <a href=\"/legal/cookies\">Read our cookie policy</a>.",
              acceptAllBtn: mode === 'opt-in' ? 'Accept all' : 'OK',
              acceptNecessaryBtn: 'Necessary only',
              showPreferencesBtn: 'Manage preferences',
              footer: '<a href="/legal/privacy">Privacy</a> &middot; <a href="/legal/cookies">Cookies</a>',
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Necessary only',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'How we use cookies',
                  description:
                    "Necessary cookies keep the site running and your session secure. Analytics cookies (off by default in the EU / UK) tell us which pages and lessons land — never who you are individually. We don't run advertising pixels.",
                },
                {
                  title: 'Necessary cookies',
                  description:
                    'Required for sign-in, security, and basic page function. Cannot be disabled.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analytics cookies',
                  description:
                    'Help us understand which pages, lessons, and steps actually work for Builders. Aggregated and pseudonymous — never sold, never matched against advertising profiles.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'More information',
                  description:
                    'See the full <a href="/legal/cookies">Cookie Policy</a> and <a href="/legal/privacy">Privacy Policy</a> for the complete picture.',
                },
              ],
            },
          },
        },
      },
    }).catch(error => console.error('Cookie consent unavailable', error));

    // No teardown — the library manages its own DOM and storage. Returning
    // a no-op cleanup keeps the effect signature consistent and React-strict-
    // mode-safe (the library guards against double-init internally).
    return undefined;
  }, []);

  // The library renders into its own portal (`#cc-main`) and creates the
  // banner DOM imperatively. This component renders nothing of its own.
  return null;
}

/**
 * Coarse heuristic for whether the visitor is probably in the EU / UK / EEA.
 * Used to default the consent mode to `opt-in` (stricter) for those
 * jurisdictions and `opt-out` (notice-and-opt-out) elsewhere.
 *
 * IP-based geolocation would be more accurate but requires a server hop
 * (Cloudflare CF-IPCountry header, MaxMind, etc.). The Accept-Language +
 * Intl timezone combination catches the common cases without an extra
 * round-trip. Anything ambiguous defaults to opt-in.
 */
function detectLikelyEuUk(): boolean {
  if (typeof window === 'undefined') return true; // SSR safe; default conservative

  const EU_UK_LANG_PREFIXES = [
    'de',
    'fr',
    'es',
    'it',
    'pt',
    'nl',
    'pl',
    'sv',
    'da',
    'fi',
    'el',
    'cs',
    'hu',
    'ro',
    'bg',
    'hr',
    'sk',
    'sl',
    'lv',
    'lt',
    'et',
    'mt',
    'ga',
    'cy',
  ];

  const navLang = (navigator.language ?? 'en').toLowerCase();
  if (navLang.startsWith('en-gb') || navLang.startsWith('en-ie')) return true;
  if (EU_UK_LANG_PREFIXES.some((prefix) => navLang.startsWith(prefix))) return true;

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    if (
      tz.startsWith('Europe/') &&
      // exclude well-known non-EU/non-UK European timezones
      !['Europe/Moscow', 'Europe/Istanbul', 'Europe/Kaliningrad'].includes(tz)
    ) {
      return true;
    }
  } catch {
    // Intl unavailable — fall through to default
  }

  return false;
}
